import { useHasSessionParentFeedback } from '@actions';
import { ReactComponent as ImprovementIcon } from '@assets/icons/V4/session-details/areas-of-improvement-icon.svg';
import { ReactComponent as FeedbackIcon } from '@assets/icons/V4/session-details/feedback-icon.svg';
import Markdown from '@components/V4/Markdown/Markdown';
import { useAuthState } from '@contexts/AuthContext';
import { SessionDetailsContext } from '@modules/SessionDetails/context/SessionDetailsContext';
import { useTranslation } from 'apps/agora/src/TranslationContext';
import { useContext } from 'react';
import { useParams } from 'react-router';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { ANALYTICS_EVENT_NAMES } from '../../utils/constants';
import { convertHexToRGBA, normalizeFeedback } from '../../utils/helpers';
import SessionDetailsEmptyState from './components/SessionDetailsEmptyState';
import SessionDetailsFeedbackLabelValue from './components/SessionDetailsFeedbackLabelValue';
import SessionDetailsLayoutCard from './components/SessionDetailsLayoutCard';

export interface FeedbackSection {
  label: string;
  value?: string;
  ratingPosition?: 'sameLine' | 'below';
  starRating?: number;
}

interface SessionDetailsFeedbackProps {
  sessionId?: string;
  meetingId?: string;
  feedbackSections: FeedbackSection[];
  isMentorFeedback?: boolean;
  hasFeedback: boolean;
  needsFeedback: boolean;
  accentColor: string;
  areasOfImprovement?: string;
  className?: string;
  setIsMentorFeedbackOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStudentFeedbackOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openParentFeedbackModalHandler?: (meetingId: string) => void;
}

const SessionDetailsFeedback = (props: SessionDetailsFeedbackProps) => {
  const {
    sessionId,
    meetingId,
    feedbackSections,
    isMentorFeedback = false,
    hasFeedback,
    needsFeedback,
    accentColor,
    areasOfImprovement,
    className,
    setIsMentorFeedbackOpen,
    setIsStudentFeedbackOpen,
    openParentFeedbackModalHandler,
  } = props;
  const { isMentor } = useAuthState();
  const { isLoading, isParentView } = useContext(SessionDetailsContext);
  const { t } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();

  const storedToken = localStorage.getItem('parentToken');

  const { data: hasSessionParentFeedback } = useHasSessionParentFeedback(
    { sessionId: sessionId ?? '', token: storedToken ?? '', userId: studentId },
    { enabled: !!sessionId && !!storedToken && isParentView }
  );
  const { trackEvent } = useAnalytics();

  const getCardTitle = () => {
    if (isMentorFeedback) {
      if (isParentView) return t('sessionDetails.mentorFeedback.title');
      return isMentor
        ? t('sessionDetails.mentorFeedback.yourFeedback')
        : t('sessionDetails.mentorFeedback.yourMentorFeedback');
    }
    if (isParentView) return t('sessionDetails.studentFeedback.title');
    return isMentor
      ? t('sessionDetails.studentFeedback.yourStudentFeedback')
      : t('sessionDetails.studentFeedback.yourFeedback');
  };

  const submitFeedbackClickHandler = () => {
    if (!needsFeedback) {
      return;
    }

    trackEvent(ANALYTICS_EVENT_NAMES.SESSION_SUBMIT_FEEDBACK_BUTTON_CLICK);

    if (isMentor) {
      setIsMentorFeedbackOpen?.(true);
      return;
    }

    setIsStudentFeedbackOpen?.(true);
  };

  const getRelevantEmptyState = () => {
    if (isMentorFeedback) {
      return isMentor ? (
        <SessionDetailsEmptyState
          icon={<FeedbackIcon className="text-customGrey" />}
          text={
            needsFeedback && !isParentView
              ? t('sessionDetails.mentorFeedback.noFeedbackWithWarning')
              : t('sessionDetails.mentorFeedback.noFeedback')
          }
          buttonVariant="primary-error"
          buttonText={
            needsFeedback && !isParentView
              ? t('sessionDetails.mentorFeedback.submitFeedback')
              : undefined
          }
          onClick={submitFeedbackClickHandler}
        />
      ) : (
        <SessionDetailsEmptyState
          icon={<FeedbackIcon className="text-customGrey" />}
          text={t('sessionDetails.mentorFeedback.noFeedbackByMentor')}
        />
      );
    }

    return isMentor ? (
      <SessionDetailsEmptyState
        icon={<FeedbackIcon className="text-customGrey" />}
        text={t('sessionDetails.studentFeedback.noFeedbackByStudent')}
      />
    ) : (
      <SessionDetailsEmptyState
        icon={<FeedbackIcon className="text-customGrey" />}
        text={t('sessionDetails.studentFeedback.noFeedback')}
        buttonText={
          needsFeedback && !isParentView
            ? t('sessionDetails.studentFeedback.submitFeedback')
            : undefined
        }
        buttonVariant="primary-error"
        onClick={submitFeedbackClickHandler}
      />
    );
  };

  const getFeedbackValue = (section: FeedbackSection) =>
    isMentorFeedback ? section.value : normalizeFeedback(section.value);

  if (isMentor) {
    return (
      <SessionDetailsLayoutCard
        isLoading={isLoading}
        className={className}
        title={getCardTitle()}
        showIcon={isParentView && !hasSessionParentFeedback && isMentorFeedback}
        icon={<FeedbackIcon className="text-white" />}
        onClick={() =>
          isParentView && !hasSessionParentFeedback && isMentorFeedback
            ? openParentFeedbackModalHandler?.(sessionId ?? '')
            : undefined
        }
        showReviewSessionBtn={isParentView && isMentorFeedback && !hasSessionParentFeedback}
      >
        {hasFeedback || areasOfImprovement ? (
          <>
            {hasFeedback && (
              <div className="flex flex-col gap-6">
                {feedbackSections.map(
                  (section, index) =>
                    (!!getFeedbackValue(section) || !!section.starRating) && (
                      <SessionDetailsFeedbackLabelValue
                        key={`${section.label}-${index}`}
                        label={section.label}
                        value={getFeedbackValue(section)}
                        starRating={section.starRating ?? undefined}
                        ratingPosition={section.ratingPosition ?? undefined}
                      />
                    )
                )}
              </div>
            )}
            {areasOfImprovement && (
              <SessionDetailsLayoutCard
                icon={<ImprovementIcon style={{ color: accentColor }} />}
                className="gap-6"
                title={t('sessionDetails.areasOfImprovement.title')}
                titleStyle={{ color: accentColor }}
                style={{ backgroundColor: convertHexToRGBA(accentColor, 10) }}
              >
                <div className="flex flex-col gap-4">
                  <Markdown text={areasOfImprovement} className="flex flex-col gap-2" />
                  <p className="text-sm text-white italic leading-5">
                    {t('sessionDetails.areasOfImprovement.aiGenerated')}
                  </p>
                </div>
              </SessionDetailsLayoutCard>
            )}
          </>
        ) : (
          getRelevantEmptyState()
        )}
      </SessionDetailsLayoutCard>
    );
  }

  return (
    <SessionDetailsLayoutCard
      isLoading={isLoading}
      className={className}
      title={getCardTitle()}
      icon={<FeedbackIcon className="text-white" />}
    >
      {hasFeedback ? (
        <div className="flex flex-col gap-6">
          {feedbackSections.map(
            (section, index) =>
              (!!getFeedbackValue(section) || !!section.starRating) && (
                <SessionDetailsFeedbackLabelValue
                  key={`${section.label}-${index}`}
                  label={section.label}
                  value={getFeedbackValue(section)}
                  starRating={section.starRating ?? undefined}
                  ratingPosition={section.ratingPosition ?? undefined}
                />
              )
          )}
        </div>
      ) : (
        getRelevantEmptyState()
      )}
    </SessionDetailsLayoutCard>
  );
};

export default SessionDetailsFeedback;
